import { notifications } from '@mantine/notifications';
import { TestType } from '@prisma/client';
import { IconAlertTriangle, IconCopy } from '@tabler/icons-react';
import { api } from '~/trpc-extra/api';
import { openDuplicateTestModal } from '~/components/test/DuplicateTestModal';

// Test types that support viewport selection during duplication
type ViewportTestType = typeof TestType.WEB | typeof TestType.WEB_MOBILE;

function isViewportSwitchable(testType: TestType): testType is ViewportTestType {
  return testType === TestType.WEB || testType === TestType.WEB_MOBILE;
}

export function useDuplicateTest({
  testId,
  projectId,
  testMeta,
  zIndex,
}: {
  testId: string;
  projectId?: string;
  testMeta?: { name: string; testType: TestType | null } | null;
  zIndex?: number;
}) {
  // Fallback mutation for MOBILE_APP tests (modal is only for WEB/WEB_MOBILE)
  const { mutate: duplicateTestDirect, isPending: isDuplicating } = api.test.duplicate.useMutation({
    onMutate: () => {
      notifications.show({
        color: 'teal',
        message: 'Duplicating test...',
        icon: <IconCopy size={16} />,
      });
    },
    onSuccess: (newTestId) => {
      notifications.show({
        color: 'teal',
        message: 'Test duplicated successfully',
        icon: <IconCopy size={16} />,
      });
      const newTestUrl = `/project/${projectId}/test/${newTestId}`;
      window.open(newTestUrl, '_blank', 'noreferrer');
    },
    onError: (error) => {
      console.error(error);
      notifications.show({
        color: 'red',
        title: 'Unable to Duplicate',
        message: 'Please contact support',
        icon: <IconAlertTriangle size={16} />,
      });
    },
  });

  const handleDuplicateClick = () => {
    if (!projectId || !testMeta) {
      return;
    }

    const testType = testMeta.testType ?? TestType.WEB;

    // For WEB/WEB_MOBILE tests, show modal to select viewport
    if (isViewportSwitchable(testType)) {
      openDuplicateTestModal({
        testId,
        projectId,
        testName: testMeta.name || 'Untitled Test',
        currentTestType: testType,
        zIndex,
      });
    } else {
      // For MOBILE_APP tests, duplicate directly
      duplicateTestDirect({ id: testId });
    }
  };

  return {
    isDuplicating,
    handleDuplicateClick,
  };
}
